import { Link, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { CATEGORIES, calculatorsByCategory, calculators } from '@/registry'
import { CalculatorCard } from '@/components/ui/CalculatorCard'
import { buildLocaleSeoLinks } from '@/i18n/seo'
import { resolveLocaleFromPath, toCurrentLocalePath } from '@/i18n/routing'
import { getCalculatorsOverviewContent } from './content'

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://toolvance.com'
const POPULAR_SLUGS = ['ohms-law', 'battery-runtime', 'electricity-cost', 'torque', 'gear-ratio', 'kinetic-energy', 'thermal-resistance', 'force']

export function CalculatorsOverviewPage() {
  const { pathname } = useLocation()
  const calcCategories = CATEGORIES.filter(c => c.group === 'calculators')
  const popular = POPULAR_SLUGS.map(s => calculators.find(c => c.slug === s)).filter(Boolean) as typeof calculators
  const seoLinks = buildLocaleSeoLinks(pathname, SITE_URL)
  const { locale } = resolveLocaleFromPath(pathname)
  const content = getCalculatorsOverviewContent(locale)
  const localize = (path: string) => toCurrentLocalePath(pathname, path)

  return (
    <>
      <Helmet>
        <title>{content.title}</title>
        <meta name="description" content={content.description} />
        <link rel="canonical" href={seoLinks.canonicalUrl} />
        {seoLinks.alternates.map(alt => (
          <link key={alt.locale} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={seoLinks.xDefaultUrl} />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
        <meta property="og:url" content={seoLinks.canonicalUrl} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to={localize('/')} className="hover:text-brand-600">{content.breadcrumbHome}</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium">{content.breadcrumbCurrent}</span>
        </nav>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">{content.heading}</h1>
        <p className="text-gray-600 mb-8">{content.intro}</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {calcCategories.map(cat => {
            const count = (calculatorsByCategory[cat.id] || []).length
            const countLabel = count === 1 ? content.countNounSingular : content.countNounPlural
            return (
              <Link
                key={cat.id}
                to={localize(`/${cat.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-5 text-center hover:border-brand-400 hover:shadow-md transition-all group"
              >
                <div className="text-3xl mb-2">{cat.icon}</div>
                <div className="font-semibold text-gray-900 group-hover:text-brand-700 text-sm">{cat.label}</div>
                <div className="text-xs text-gray-400 mt-1">{count} {countLabel}</div>
              </Link>
            )
          })}
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-4">{content.popularHeading}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {popular.map(c => <CalculatorCard key={c.slug} calc={c} />)}
        </div>
      </div>
    </>
  )
}
